/**
 * permdial.mjs — six real permission modes, and what each one does to a proposed action.
 *
 * FOUNDER, 2026-08-15: "add a module in end for mastering claudecode zero to hero" ·
 * "ensure course is world class and not paper and texty".
 *
 * ── THE CONCEPT MEETS ITS IMPLEMENTATION ──────────────────────────────────────────────────────
 * CodeAI101 chapter 3 taught three approval tiers: auto-approve, approve-after, approve-before.
 * A clean idea. The tool a learner actually runs offers SIX named modes, read from `claude --help`
 * on a real machine and held in ccfacts.mjs where a gate re-checks them.
 *
 * That gap between three and six IS the chapter. Not because the concept was wrong — it is exactly
 * right — but because real tools have modes for situations a three-way split does not name, and a
 * learner who knows only the concept will pick from a menu they do not recognise. This lab puts the
 * six on screen, shows what each does to the same proposed action, and marks which of the three
 * concept-tiers each one belongs to.
 *
 * ── THE MAPPING IS THE LAB'S READING, AND IS LABELLED ─────────────────────────────────────────
 * The six NAMES are the tool's, verified. The mapping from each name to a CodeAI101 tier is this
 * lab's interpretation, and every place it appears it says so. What each mode does in fine detail
 * is the tool's behaviour, not mine to assert beyond what the help text states — so where the help
 * text is silent, this lab is silent and says where to look instead.
 *
 * ── NOTHING IS EXECUTED, AND NO MODE IS RECOMMENDED ───────────────────────────────────────────
 * The lab runs no `claude` command. It also does not tell a learner which mode to use: that is a
 * judgement about their situation, and the one genuinely dangerous position on the dial is named
 * as dangerous rather than hidden — a course that quietly omitted bypassPermissions would be
 * teaching a menu the tool does not have.
 */

import { PERMISSION_MODES, VERSION, CAPTURED_ON } from './ccfacts.mjs';

export const SOURCE_TOOL = `Claude Code ${VERSION}, \`claude --help\` — read ${CAPTURED_ON}`;
export const SOURCE_CONCEPT = 'CodeAI101 module 03 §5 "Approval tiers — who says yes"';

/** CodeAI101's three, as the concept the learner arrives with. */
export const CONCEPT_TIERS = Object.freeze([
  { id: 'auto', name: 'Auto-approve', gist: 'the harness runs the call as soon as the AI proposes it' },
  { id: 'after', name: 'Approve-after', gist: 'it runs, and you review what happened' },
  { id: 'before', name: 'Approve-before', gist: 'you see the proposal and nothing runs until you say so' },
]);

/**
 * THE SIX. Names verified against the tool; the `tier` and `plain` fields are this lab's reading.
 *
 * `certainty` is deliberate: 'named' means the help text names the mode and nothing more, so the
 * description is an inference from the name and must be read as one. A course that stated confident
 * behaviour for a mode it had only seen listed would be doing the exact thing CodeAI101 chapter 1
 * warns about.
 */
export const MODES = Object.freeze([
  { id: 'manual', tier: 'before', certainty: 'named',
    plain: 'Ask before acting. The safest position and the slowest.',
    feels: 'Every edit waits for you. Nothing surprises you and nothing is quick.' },
  { id: 'plan', tier: 'before', certainty: 'named',
    plain: 'Work out what it would do, and present that instead of doing it.',
    feels: 'You get a plan to argue with before any file is touched.' },
  { id: 'acceptEdits', tier: 'auto', certainty: 'named',
    plain: 'File edits go through without asking. Other actions still ask.',
    feels: 'Fast where it is usually safe, still gated where it usually is not.' },
  { id: 'auto', tier: 'after', certainty: 'named',
    plain: 'The tool decides when to ask, rather than you setting one rule for everything.',
    feels: 'Fewer interruptions than manual, and you are trusting its judgement about which ones matter.' },
  { id: 'dontAsk', tier: 'auto', certainty: 'named',
    plain: 'Stop asking. Actions proceed without prompting you.',
    feels: 'Quiet. You find out what happened by reading, not by being asked.' },
  { id: 'bypassPermissions', tier: 'auto', certainty: 'named', dangerous: true,
    plain: 'The permission layer is not consulted.',
    feels: 'Nothing stands between a proposed action and your machine. This is the position CodeAI101 chapter 3 spends a whole section on: a wrong action, not a wrong sentence.' },
]);

/** The mapping is an interpretation and every surface must say so. */
export const MAPPING_IS_A_READING = 'The six names are the tool\'s, read from `claude --help` and re-checked by a gate. Which CodeAI101 tier each one belongs to is this lab\'s reading of the names — useful for orientation, and not something the tool declares.';
export const WHERE_TO_LOOK = 'For exactly what a mode does in every case, run `claude --help` yourself and read `/help` inside a session. This lab teaches the shape of the dial, not a substitute for the tool\'s own word.';

/**
 * What happens to a proposed action under a given mode.
 *
 * `stops` is the only thing asserted, and it follows from the tier mapping rather than from any
 * claim about internals: a before-tier mode holds the action, an auto-tier mode does not. Where the
 * lab does not know, it returns 'unknown' rather than guessing — which is the whole point.
 */
export function underMode(modeId, action = { kind: 'edit', reversible: true }) {
  const m = MODES.find((x) => x.id === modeId);
  if (!m) return null;
  const stops = m.tier === 'before';
  /* acceptEdits is the one mode whose behaviour genuinely DEPENDS on what kind of action it is —
     the help text distinguishes edits from everything else, so the lab distinguishes them too. */
  const dependsOnKind = m.id === 'acceptEdits';

  /* ── THE HONEST 'DEPENDS', AND WHY IT IS HERE ──────────────────────────────────────────────
     `auto` is described by the tool as deciding for itself when to ask. The first version of this
     function reported it as PROCEEDS on every action, including deleting an unversioned directory
     — a confident claim about behaviour the help text does not state, printed in a course whose
     chapter 1 is about exactly that failure. It may well ask on a destructive action; I do not
     know, and the lab must not pretend to. It returns 'depends' and says where to find out. */
  if (m.id === 'auto') {
    return {
      mode: m, held: null, outcome: 'depends — the tool decides',
      irreversibleWarning: !action.reversible,
      note: 'This mode decides for itself when to ask, so what happens here is not something the help text states. Run it and watch, or use a mode whose answer is fixed if you need to know in advance.',
    };
  }

  const held = dependsOnKind ? action.kind !== 'edit' : stops;
  return {
    mode: m, held,
    outcome: held ? 'held for you' : 'proceeds',
    irreversibleWarning: held === false && !action.reversible,
    note: dependsOnKind
      ? 'This mode treats edits differently from other actions — the only one of the six that does.'
      : null,
  };
}

/** Three actions of escalating consequence. Deliberately ordinary — this is a real day's work. */
export const ACTIONS = Object.freeze([
  { id: 'edit', kind: 'edit', reversible: true, label: 'Edit a source file', detail: 'change three lines in a file that is committed to git' },
  { id: 'install', kind: 'command', reversible: true, label: 'Run an install command', detail: 'add a dependency to the project' },
  { id: 'delete', kind: 'command', reversible: false, label: 'Delete a directory', detail: 'remove a folder that is NOT in version control' },
]);

/** Verified: the six the lab shows are the six the tool offers. */
export const modesMatchTool = () =>
  MODES.length === PERMISSION_MODES.length
  && MODES.every((m) => PERMISSION_MODES.includes(m.id));
